// dependencies
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ObjectId } from "bson";
import React, { ReactNode } from "react";
import { useParams } from "react-router-dom";

import { faGripDotsVertical, faPencil } from "@fortawesome/pro-regular-svg-icons";
import { faClone, faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";

import { ArrayFieldState, Icon, SubmitButton } from "@performyard/py-components";
import { SurveyQuestionJson } from "@performyard/service-contracts";

//components
import Button from "../../../../../../../../components/Button";
import Spacer from "../../../../../../../../components/Spacer";

import { useSurveyStatus } from "../../../../store/store";
import { useOrderedQuestions, useSetters } from "../../store/store";

import { useSurveyAccessTier } from "../../../../hooks/useSurveyAccessTier";

//store
import MultipleChoiceEdit from "../EditQuestionBlock/questions/multiple-choice";
import RatingScaleEdit from "../EditQuestionBlock/questions/rating-scale";

export type QuestionTypeMap = {
	text: SurveyQuestionJson;
};

export type EditQuestionBlockProps = {
	data: SurveyQuestionJson;
	index: number;
	publishedQuestion: boolean;
	mutateField: (value: SurveyQuestionJson) => void;
};

export const questionDisplayMap: {
	[key: string]: {
		label: string;
		edit: (EditQuestionBlockProps) => ReactNode;
	};
} = {
	text: { label: "Long Answer", edit: () => null },
	"rating-scale": {
		label: "Rating Scale",
		edit: (props: EditQuestionBlockProps) => <RatingScaleEdit {...props} />,
	},
	"multiple-choice": {
		label: "Multiple Choice",
		edit: (props: EditQuestionBlockProps) => <MultipleChoiceEdit {...props} />,
	},
	"multiple-select": {
		label: "Multiple Choice",
		edit: (props: EditQuestionBlockProps) => <MultipleChoiceEdit {...props} />,
	},
	instructions: {
		label: "Instructions",
		edit: () => null,
	},
};

type QuestionBlockProps = {
	data: QuestionTypeMap[keyof QuestionTypeMap];
	field: ArrayFieldState<SurveyQuestionJson>;
	index: number;
	_id: ObjectId;
};

const QuestionBlock = ({ data, field, index, _id }: QuestionBlockProps) => {
	const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: data._id });

	// store
	const setters = useSetters();
	const orderedQuestions = useOrderedQuestions();
	const surveyStatus = useSurveyStatus();
	const { surveyId = "" } = useParams();
	const surveyUserAccess = useSurveyAccessTier(surveyId).tier;

	const style = {
		transform: CSS.Translate.toString(transform),
		transition,
	};

	const handleDeleteQuestion = () => {
		if (surveyStatus !== "draft" && orderedQuestions.length === 1) {
			setters.setBuilderBanner({
				message: "Published surveys require at least one question at all times.",
				color: "red",
				icon: faExclamationTriangle,
			});
		} else {
			setters.setHasChanges(true);
			field.removeValue(index);
		}
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			{...attributes}
			className="mb-4 grid grid-cols-[24px_1fr] justify-center border-[1px] border-solid border-neutral-2 bg-neutral-0"
		>
			<div
				{...listeners}
				className="flex w-6 cursor-grab items-center justify-center border-r-[1px] border-solid border-neutral-2 text-neutral-4 hover:text-neutral-8 active:cursor-grabbing"
			>
				<Icon
					size={24}
					icon={faGripDotsVertical}
					color="neutral-7"
					dangerouslySetClassName="hover:text-neutral-9"
				/>
			</div>

			<div className="max-w-[calc(100% - 24px)] group min-w-0 break-words p-4">
				<div className="mb-2 flex h-8 items-start justify-between">
					<p className="text-paragraph-3 font-semi-bold">
						{questionDisplayMap[data?.type]?.label}
						{data?.type === "multiple-select" && (
							<span className="font-normal text-neutral-6"> (Supports Multiple Selections)</span>
						)}
					</p>
					<div className="hidden gap-2 group-hover:flex">
						{surveyStatus !== "closed" && surveyUserAccess === "edit" && (
							<>
								<SubmitButton
									color="neutral"
									iconLeft={faPencil}
									variant="outline"
									onSubmitMeta={{
										action: "edit-question",
										questionIndex: index,
										_id,
										metaAction: () => {
											setters.setBuilderBanner(null);
											setters.setEditingQuestion({ _id, isNew: false });
										},
									}}
								>
									Edit
								</SubmitButton>
								<SubmitButton
									color="neutral"
									iconLeft={faClone}
									variant="outline"
									onSubmitMeta={{
										action: "duplicate-question",
										questionIndex: index,
										metaAction: () => {
											const newQuestion: SurveyQuestionJson = {
												...data,
												_id: new ObjectId().toString(),
											};

											if (
												["multiple-choice", "multiple-select", "rating-scale"].includes(
													data.type
												)
											) {
												newQuestion.options = data.options?.map((option) => {
													if (typeof option === "string") {
														return option;
													}
													return {
														...option,
														_id: new ObjectId().toString(),
													};
												});
											}

											field.insertValue(index + 1, newQuestion);
											setters.setBuilderBanner(null);
										},
									}}
								>
									Duplicate
								</SubmitButton>
								<Button
									testid="delete-question"
									color="red"
									size="medium"
									onClick={handleDeleteQuestion}
								>
									Delete
								</Button>
							</>
						)}
					</div>
				</div>

				<h4 className="max-w-full overflow-hidden text-wrap break-words text-paragraph-1 font-semi-bold">
					{data?.text}
				</h4>
				{data?.subtitle ? (
					<>
						<Spacer height={1} />
						<div className="is-markdown whitespace-pre-wrap text-paragraph-3 text-neutral-7">
							{data.subtitle}
						</div>
					</>
				) : null}
				{data?.subtitle ? null : <Spacer height={2} />}
			</div>
		</div>
	);
};

export default QuestionBlock;
